import { forwardRef, ReactNode, useRef } from "react";

import { DesktopDatePicker, DesktopDatePickerProps } from "@mui/x-date-pickers/DesktopDatePicker";

type TDate = Date | null;

const mapUTCDateToLocal = (date: Date): Date => {
    const localDate = new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds(),
    );
    return localDate;
};

const mapLocalDateToUTC = (date: Date): Date => {
    const utcTimestamp = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds(),
    );
    return new Date(utcTimestamp);
};

type DatePickerProps = Omit<DesktopDatePickerProps<Date>, "value" | "onChange"> & {
    value?: TDate;
    onChange?: (value: TDate) => void;
    onClick?: () => void;
    clearable?: boolean;
    error?: boolean;
    helperText?: ReactNode;
};

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
    (
        {
            value,
            onChange = () => void 0,
            minDate = new Date("2000-01-01"),
            clearable = true,
            error = false,
            helperText = null,
            ...props
        },
        inputRef,
    ) => {
        const buttonRef = useRef<HTMLButtonElement>(null);

        return (
            <DesktopDatePicker
                {...props}
                value={value && mapUTCDateToLocal(new Date(value))}
                minDate={minDate}
                slotProps={{
                    openPickerButton: {
                        ref: buttonRef,
                    },
                    field: {
                        inputRef,
                        clearable,
                        onClear: () => onChange(null),
                    },
                    textField: {
                        error,
                        helperText,
                        onClick: () => buttonRef.current?.click(),
                        onKeyDown: (e) => e.preventDefault(),
                    },
                }}
                onChange={(date) => {
                    onChange(date && mapLocalDateToUTC(date));
                }}
            />
        );
    },
);
